import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Database types
export interface Database {
  public: {
    Tables: {
      user_profiles: {
        Row: {
          id: string;
          email: string;
          timezone: string;
          created_at: string;
          last_login: string;
        };
        Insert: {
          id: string;
          email: string;
          timezone?: string;
          created_at?: string;
          last_login?: string;
        };
        Update: {
          id?: string;
          email?: string;
          timezone?: string;
          last_login?: string;
        };
      };
      goals: {
        Row: {
          id: string;
          user_id: string;
          daily_calories: number | null;
          daily_protein: number | null;
          weekly_workout_days: number | null;
          daily_workout_minutes: number | null;
          target_weight: number | null;
          weight_goal_type: 'lose' | 'gain' | 'maintain' | null;
          activity_level: string | null;
          dietary_preferences: string[] | null;
          health_conditions: string[] | null;
          available_equipment: string[] | null;
          preferred_workout_times: string[] | null;
          meal_prep_frequency: string | null;
          cooking_skill_level: string | null;
          budget_per_week: number | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          daily_calories?: number | null;
          daily_protein?: number | null;
          weekly_workout_days?: number | null;
          daily_workout_minutes?: number | null;
          target_weight?: number | null;
          weight_goal_type?: 'lose' | 'gain' | 'maintain' | null;
          activity_level?: string | null;
          dietary_preferences?: string[] | null;
          health_conditions?: string[] | null;
          available_equipment?: string[] | null;
          preferred_workout_times?: string[] | null;
          meal_prep_frequency?: string | null;
          cooking_skill_level?: string | null;
          budget_per_week?: number | null;
        };
        Update: {
          daily_calories?: number | null;
          daily_protein?: number | null;
          weekly_workout_days?: number | null;
          daily_workout_minutes?: number | null;
          target_weight?: number | null;
          weight_goal_type?: 'lose' | 'gain' | 'maintain' | null;
          activity_level?: string | null;
          dietary_preferences?: string[] | null;
          health_conditions?: string[] | null;
          available_equipment?: string[] | null;
          preferred_workout_times?: string[] | null;
          meal_prep_frequency?: string | null;
          cooking_skill_level?: string | null;
          budget_per_week?: number | null;
          updated_at?: string;
        };
      };
      onboarding_data: {
        Row: {
          id: string;
          user_id: string;
          current_weight: number | null;
          target_weight: number | null;
          height_feet: number | null;
          height_inches: number | null;
          age: number | null;
          gender: string | null;
          activity_level: string | null;
          weight_goal: string | null;
          dietary_preferences: string[] | null;
          health_conditions: string[] | null;
          available_equipment: string[] | null;
          workout_preferences: string[] | null;
          meal_prep_time: string | null;
          cooking_skill: string | null;
          budget_range: string | null;
          completed: boolean;
          completed_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          current_weight?: number | null;
          target_weight?: number | null;
          height_feet?: number | null;
          height_inches?: number | null;
          age?: number | null;
          gender?: string | null;
          activity_level?: string | null;
          weight_goal?: string | null;
          dietary_preferences?: string[] | null;
          health_conditions?: string[] | null;
          available_equipment?: string[] | null;
          workout_preferences?: string[] | null;
          meal_prep_time?: string | null;
          cooking_skill?: string | null;
          budget_range?: string | null;
          completed?: boolean;
          completed_at?: string | null;
        };
        Update: {
          current_weight?: number | null;
          target_weight?: number | null;
          height_feet?: number | null;
          height_inches?: number | null;
          age?: number | null;
          gender?: string | null;
          activity_level?: string | null;
          weight_goal?: string | null;
          dietary_preferences?: string[] | null;
          health_conditions?: string[] | null;
          available_equipment?: string[] | null;
          workout_preferences?: string[] | null;
          meal_prep_time?: string | null;
          cooking_skill?: string | null;
          budget_range?: string | null;
          completed?: boolean;
          completed_at?: string | null;
          updated_at?: string;
        };
      };
      meal_plans: {
        Row: {
          id: string;
          user_id: string;
          date: string;
          meal_type: 'breakfast' | 'lunch' | 'dinner' | 'snack';
          description: string;
          calories: number;
          protein: number;
          location: string;
          source: string | null;
          cook_within: string | null;
          expires_at: string | null;
          notes: string | null;
          completed: boolean;
          sent_to_log: boolean;
          completed_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          date: string;
          meal_type: 'breakfast' | 'lunch' | 'dinner' | 'snack';
          description: string;
          calories: number;
          protein: number;
          location?: string;
          source?: string | null;
          cook_within?: string | null;
          expires_at?: string | null;
          notes?: string | null;
          completed?: boolean;
          sent_to_log?: boolean;
          completed_at?: string | null;
        };
        Update: {
          date?: string;
          meal_type?: 'breakfast' | 'lunch' | 'dinner' | 'snack';
          description?: string;
          calories?: number;
          protein?: number;
          location?: string;
          source?: string | null;
          cook_within?: string | null;
          expires_at?: string | null;
          notes?: string | null;
          completed?: boolean;
          sent_to_log?: boolean;
          completed_at?: string | null;
          updated_at?: string;
        };
      };
      workout_plans: {
        Row: {
          id: string;
          user_id: string;
          date: string;
          workout_type: string;
          description: string;
          duration_minutes: number | null;
          calories_burned: number | null;
          distance: number | null;
          distance_unit: string | null;
          location: string;
          focus_area: string | null;
          intensity: string | null;
          notes: string | null;
          completed: boolean;
          sent_to_log: boolean;
          completed_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          date: string;
          workout_type: string;
          description: string;
          duration_minutes?: number | null;
          calories_burned?: number | null;
          distance?: number | null;
          distance_unit?: string | null;
          location?: string;
          focus_area?: string | null;
          intensity?: string | null;
          notes?: string | null;
          completed?: boolean;
          sent_to_log?: boolean;
          completed_at?: string | null;
        };
        Update: {
          date?: string;
          workout_type?: string;
          description?: string;
          duration_minutes?: number | null;
          calories_burned?: number | null;
          distance?: number | null;
          distance_unit?: string | null;
          location?: string;
          focus_area?: string | null;
          intensity?: string | null;
          notes?: string | null;
          completed?: boolean;
          sent_to_log?: boolean;
          completed_at?: string | null;
          updated_at?: string;
        };
      };
      log_entries: {
        Row: {
          id: string;
          user_id: string;
          date: string;
          entry_type: 'meal' | 'workout';
          meal_type: 'breakfast' | 'lunch' | 'dinner' | 'snack' | null;
          workout_type: string | null;
          description: string;
          calories: number | null;
          protein: number | null;
          duration_minutes: number | null;
          calories_burned: number | null;
          distance: number | null;
          distance_unit: string | null;
          location: string | null;
          focus_area: string | null;
          intensity: string | null;
          notes: string | null;
          source: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          date: string;
          entry_type: 'meal' | 'workout';
          meal_type?: 'breakfast' | 'lunch' | 'dinner' | 'snack' | null;
          workout_type?: string | null;
          description: string;
          calories?: number | null;
          protein?: number | null;
          duration_minutes?: number | null;
          calories_burned?: number | null;
          distance?: number | null;
          distance_unit?: string | null;
          location?: string | null;
          focus_area?: string | null;
          intensity?: string | null;
          notes?: string | null;
          source?: string;
        };
        Update: {
          date?: string;
          entry_type?: 'meal' | 'workout';
          meal_type?: 'breakfast' | 'lunch' | 'dinner' | 'snack' | null;
          workout_type?: string | null;
          description?: string;
          calories?: number | null;
          protein?: number | null;
          duration_minutes?: number | null;
          calories_burned?: number | null;
          distance?: number | null;
          distance_unit?: string | null;
          location?: string | null;
          focus_area?: string | null;
          intensity?: string | null;
          notes?: string | null;
          source?: string;
          updated_at?: string;
        };
      };
      vitals: {
        Row: {
          id: string;
          user_id: string;
          date: string;
          weight: number | null;
          weight_unit: string;
          systolic: number | null;
          diastolic: number | null;
          notes: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          date: string;
          weight?: number | null;
          weight_unit?: string;
          systolic?: number | null;
          diastolic?: number | null;
          notes?: string | null;
        };
        Update: {
          date?: string;
          weight?: number | null;
          weight_unit?: string;
          systolic?: number | null;
          diastolic?: number | null;
          notes?: string | null;
          updated_at?: string;
        };
      };
    };
  };
}
